import React, { useEffect, useState, FC } from 'react';

import { noop } from 'lodash';
import { AxiosInstance } from 'axios';
import { createBridgeComponent } from 'mfe-react-bridge';
import { Spinner } from 'mintel-react-ui';

import { CustomAccessType, CustomPanelType } from '/types';

import useTranslate from '../../../utils/translations';
import AccessList from './AccessList';
import styles from './AccessPanel.scss';
import useFetchCustomAccess from './useFetchCustomAccess';
import { useCwaAxios } from '../../../utils/cwaAxios';
import useTracking from '../../../utils/useTracking';

type AccessPanelProps = {
  articleAccessUrl: string;
  cwaAxios: AxiosInstance;
  trackAccessPanel: () => void;
};

const AccessPanelComponent: FC<AccessPanelProps> = ({
  articleAccessUrl,
  cwaAxios,
  trackAccessPanel,
}) => {
  const [access, setAccess] = useState([] as CustomAccessType[]);

  useFetchCustomAccess(cwaAxios, articleAccessUrl, setAccess);
  useEffect(() => {
    trackAccessPanel();
  }, [trackAccessPanel]);

  return (
    <div className={styles.container}>
      {!!access.length && <div className={styles['scroll-gradient']} />}
      <div className={styles.lists}>
        {!!access.length ? (
          access.map(({ key, title, accessItems }: CustomAccessType) => (
            <AccessList key={key} title={title} accessItems={accessItems} />
          ))
        ) : (
          <div className={styles.spinner}>
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
};

const useAccessPanel = (articleAccessUrl: string): CustomPanelType => {
  const t = useTranslate();
  const cwaAxios = useCwaAxios();
  const { trackAccessPanel } = useTracking();

  return {
    bridge: {
      default: createBridgeComponent({
        rootComponent: (): JSX.Element => (
          <AccessPanelComponent
            articleAccessUrl={articleAccessUrl}
            cwaAxios={cwaAxios}
            trackAccessPanel={trackAccessPanel}
          />
        ),
      }),
    },
    closeSidebar: noop,
    color: 'tertiary',
    iconName: 'key',
    title: t('Access'),
    subtitle: t(
      'This content piece can be accessed by the organisations, groups and individuals listed below. The Project Owner can change the client access settings through the CMS.',
    ),
    width: 800,
    id: 'access-panel',
  };
};

export default useAccessPanel;
